/**
 * Responsibility: Implement quest check behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { QuestStatus } from "@anyquest/shared-core"
import type { SupervisorCheckFn, SupervisorIncidentSeed } from "@anyquest/shared-core"

const VALID_STATUS: QuestStatus[] = ['active', 'completed', 'failed', 'dormant', 'potential']

export const questCheck: SupervisorCheckFn = (state) => {
  const incidents: SupervisorIncidentSeed[] = []
  const seenIds = new Set<string>()
  const dupIds: string[] = []
  let invalidStatus = 0
  let terminalMissingCompleted = 0
  let danglingDeps = 0

  const ids = new Set((state.journal ?? []).map(q => String(q?.id ?? '').trim()).filter(Boolean))

  for (const quest of state.journal ?? []) {
    const id = String(quest?.id ?? '').trim()
    if (id) {
      if (seenIds.has(id)) dupIds.push(id)
      else seenIds.add(id)
    }

    if (!VALID_STATUS.includes(quest.status as QuestStatus)) invalidStatus += 1

    if ((quest.status === 'completed' || quest.status === 'failed') && !Number.isFinite(quest.completed)) {
      terminalMissingCompleted += 1
    }

    const deps = Array.isArray(quest.dependsOn) ? quest.dependsOn : []
    for (const dep of deps) {
      if (!ids.has(String(dep ?? '').trim())) danglingDeps += 1
    }
  }

  if (dupIds.length > 0) {
    incidents.push({
      code: 'quest_duplicate_ids',
      domain: 'quest',
      severity: 'medium',
      confidence: 0.99,
      message: `${dupIds.length} duplicate quest IDs detected.`,
      remediationPlan: 'Keep first quest per ID and prune duplicates.',
      context: { ids: dupIds.slice(0, 15) },
    })
  }

  if (invalidStatus > 0) {
    incidents.push({
      code: 'quest_invalid_status',
      domain: 'quest',
      severity: 'high',
      confidence: 0.99,
      message: `${invalidStatus} quests have invalid status values.`,
      remediationPlan: 'Normalize invalid statuses to dormant.',
      context: { invalidStatus },
    })
  }

  if (terminalMissingCompleted > 0) {
    incidents.push({
      code: 'quest_terminal_missing_completed_timestamp',
      domain: 'quest',
      severity: 'low',
      confidence: 0.97,
      message: `${terminalMissingCompleted} terminal quests are missing completion timestamps.`,
      remediationPlan: 'Backfill completion minute from current world clock.',
      context: { terminalMissingCompleted },
    })
  }

  if (danglingDeps > 0) {
    incidents.push({
      code: 'quest_dangling_dependencies',
      domain: 'quest',
      severity: 'low',
      confidence: 0.94,
      message: `${danglingDeps} quest dependencies reference unknown quest IDs.`,
      remediationPlan: 'Drop dangling dependency IDs.',
      context: { danglingDeps },
    })
  }

  return { passed: incidents.length === 0, incidents }
}
